import { forEach, get } from 'lodash-es';
import { EventToolbox } from 'client/utils/event-toolbox';
import { TrackingConstant } from 'client/tracking/constant';
import { getCreativeId } from 'client/engagement-handlers/helper';
import { FINANCING_EVENTS } from 'site-modules/shared/constants/financing/financing-events';
import { BASICS_STEP, INTRO_STEP, STEPS_MAP } from 'site-modules/shared/constants/financing/steps';
import { INDIVIDUAL, JOINT } from 'site-modules/shared/constants/financing/financing-constants';
import { checkCounters } from 'site-modules/shared/utils/financing/financing';
import { CONSUMER_EMAIL_VALUES, COUNTER_TYPE } from 'site-modules/shared/constants/financing/financing';
import { updateCreativeId } from 'site-modules/financing/utils/update-creative-id';
import { getInvId, getInvVin } from 'client/engagement-handlers/list-engagement-handler/list-engagement-handler';
import { isPartnerDealer } from 'site-modules/shared/utils/dealer-details-utils';
import { getInventoryTypesCount } from 'site-modules/shared/utils/inventory-utils/get-inventory-types-count';
import {
  DATA_PATH,
  DEFAULT_VALUE,
  MATCH_RESULT,
  NEGATIVE,
  PHOTO_TYPE,
  POSITIVE,
} from 'client/engagement-handlers/inventory-engagement-handler/constants';
import { isHistoryChanged } from 'site-modules/shared/utils/inventory-utils/is-history-changed';
import { getCorrectStatus } from 'site-modules/shared/components/inventory/helpers/vehicle-history-helper';
import { updateCreativeIdForConfirmMode } from 'site-modules/financing/utils/update-creative-id-for-confirm-mode';
import { HTTP_OK } from 'client/utils/http-status';

/**
 * Returns passed true/false values based on passed statement.
 * @param statement
 * @param truePath
 * @param falsePath
 * @returns {*}
 */
export function verifyStatement(statement, truePath, falsePath) {
  return statement ? truePath : falsePath;
}

function fireFormFieldTracking({ detail: { target, data } }) {
  const { isValidated, isPrimary } = data;
  const fieldName = get(data, 'name', target.getAttribute('name'));
  const verdict = verifyStatement(isValidated, 'success', 'failure');
  const suffix = verifyStatement(!!isPrimary, 'Primary', '');

  const trackData = {
    event_type: TrackingConstant.EVENT_TYPE_ACTION_PROGRESS,
    event_data: {
      action_name: TrackingConstant.ACTION_FILL_FORM,
      action_cause: TrackingConstant.ACTION_CAUSE_USER_INPUT,
      action_category: TrackingConstant.USER_ACTION_CATEGORY,
      subaction_name: `${fieldName + suffix}_input_${verdict}`,
      creative_id: getCreativeId(target),
    },
  };

  EventToolbox.fireTrackAction(trackData);
}

function fireCalculatePaymentsButton({ detail: { monthlyPayment, selectedTerm, isPageLoad } }) {
  const actionCause = verifyStatement(
    !!isPageLoad,
    TrackingConstant.PAGE_LOAD_CAUSE,
    TrackingConstant.ACTION_CAUSE_BUTTON_CLICK
  );
  const trackData = {
    event_type: TrackingConstant.EVENT_TYPE_ACTION_COMPLETED,
    event_data: {
      action_name: TrackingConstant.CALCULATE_PAYMENT,
      subaction_name: TrackingConstant.CALCULATE_PAYMENT,
      action_cause: actionCause,
      action_category: TrackingConstant.USER_ACTION_CATEGORY,
      creative_id: 'payment_inputs',
      value: `${monthlyPayment} of ${selectedTerm}`,
    },
  };

  EventToolbox.fireTrackAction(trackData);
}

function firePaymentListPixel({ detail: { offers, isMatchingVIN } }) {
  const { payment, apr, term, lenderNames } = offers.reduce(
    (result, offer) => {
      const isCounterApplied = checkCounters(offer);
      if (isCounterApplied) {
        result.payment.push(0);
        result.apr.push(0);
      } else {
        result.payment.push(Math.round(get(offer, 'financeInformation.monthlyPayment', 0)));
        result.apr.push(get(offer, 'financeInformation.buyRate', 0).toFixed(2));
      }

      result.term.push(get(offer, 'financeInformation.loanTerm', 0));
      result.lenderNames.push(get(offer, 'lenderId'));
      return result;
    },
    { payment: [], apr: [], term: [], lenderNames: [] }
  );
  const trackData = {
    event_type: TrackingConstant.EVENT_TYPE_ACTION_COMPLETED,
    event_data: {
      action_name: TrackingConstant.ACTION_LIST,
      lender_name: lenderNames.join(', '),
      match_results: offers.length,
      monthly_payments: payment.join(', '),
      aprs: apr.join(', '),
      terms: term.join(', '),
      creative_id: updateCreativeId({ isMatchingVIN, trackingParent: 'payment_terms' }),
    },
  };

  EventToolbox.fireTrackAction(trackData);
}

function fireNextStepBtn({ detail: { activeStep, isJoint, isMatchingVIN, isPreVinFlow } }) {
  let trackData;
  const value = verifyStatement(isJoint, JOINT, INDIVIDUAL);
  const creativeId = updateCreativeId({
    isPreVinFlow,
    isMatchingVIN,
    trackingParent: verifyStatement(activeStep === BASICS_STEP, BASICS_STEP, `financing_${activeStep}_${value}`),
  });

  const commonEventData = {
    action_name: TrackingConstant.ACTION_FILL_FORM,
    action_cause: TrackingConstant.ACTION_CAUSE_BUTTON_CLICK,
    action_category: TrackingConstant.USER_ACTION_CATEGORY,
    value,
    creative_id: creativeId,
  };

  if (activeStep === INTRO_STEP) {
    trackData = {
      event_type: TrackingConstant.EVENT_TYPE_ACTION_START,
      event_data: {
        action_name: TrackingConstant.ACTION_FILL_FORM,
        subaction_name: TrackingConstant.START_FINANCING_APP,
        action_category: TrackingConstant.USER_ACTION_CATEGORY,
        action_cause: TrackingConstant.ACTION_CAUSE_BUTTON_CLICK,
        creative_id: updateCreativeId({ isPreVinFlow, isMatchingVIN, trackingParent: 'intro' }),
        value: 'Get Started',
      },
    };
  } else if (activeStep !== BASICS_STEP) {
    trackData = {
      event_type: TrackingConstant.EVENT_TYPE_ACTION_PROGRESS,
      event_data: { subaction_name: TrackingConstant.SUBACTION_CONTINUE_TO_NEXT_SCREEN, ...commonEventData },
    };
  } else {
    trackData = {
      event_type: TrackingConstant.EVENT_TYPE_ACTION_PROGRESS,
      event_data: { subaction_name: TrackingConstant.SUBACTION_SELECT_APP_TYPE, ...commonEventData },
    };
  }

  EventToolbox.fireTrackAction(trackData);
}

function fireSaveVehicle({ detail: { vin } }) {
  EventToolbox.fireTrackAction({
    event_type: TrackingConstant.EVENT_TYPE_ACTION_COMPLETED,
    event_data: {
      action_cause: TrackingConstant.ACTION_CAUSE_BUTTON_CLICK,
      action_category: TrackingConstant.USER_ACTION_CATEGORY,
      action_name: TrackingConstant.SAVE_VEHICLE,
      subaction_name: TrackingConstant.SAVE_VIN,
      value: vin,
    },
  });
}

function fireBackBtn({ detail: { activeStep, isJoint, isMatchingVIN, isPreVinFlow } }) {
  const currentStep = STEPS_MAP[activeStep];
  const prevPath = get(currentStep, 'prevPath');
  const suffix = verifyStatement(isJoint, JOINT, INDIVIDUAL);
  const creativeId = updateCreativeId({
    isPreVinFlow,
    isMatchingVIN,
    trackingParent: verifyStatement(prevPath === BASICS_STEP, prevPath, `${prevPath}_${suffix}`),
  });

  const trackData = {
    event_type: TrackingConstant.EVENT_TYPE_ACTION_PROGRESS,
    event_data: {
      action_name: TrackingConstant.ACTION_FILL_FORM,
      subaction_name: TrackingConstant.SUBACTION_VIEW_FINANCING_APP,
      action_cause: TrackingConstant.ACTION_CAUSE_BUTTON_CLICK,
      action_category: TrackingConstant.USER_ACTION_CATEGORY,
      creative_id: creativeId,
    },
  };

  EventToolbox.fireTrackAction(trackData);
}

function fireLeadFormAttemptSubmit({ target }) {
  EventToolbox.fireTrackAction({
    event_type: TrackingConstant.EVENT_TYPE_ACTION_PROGRESS,
    event_data: {
      action_cause: TrackingConstant.ACTION_CAUSE_BUTTON_CLICK,
      action_category: TrackingConstant.USER_ACTION_CATEGORY,
      action_name: TrackingConstant.ACTION_LEAD_SUBMISSION,
      subaction_name: TrackingConstant.ATTEMPT_SUBMIT,
      creative_id: getCreativeId(target),
      value: 'Send to Dealer',
    },
  });
}

function fireLeadFormProcessedSubmit({ target }) {
  EventToolbox.fireTrackAction({
    event_type: TrackingConstant.EVENT_TYPE_ACTION_COMPLETED,
    event_data: {
      action_cause: TrackingConstant.SYSTEM_ACTION_CAUSE,
      action_category: TrackingConstant.SYSTEM_ACTION_CATEGORY,
      action_name: TrackingConstant.ACTION_LEAD_SUBMISSION,
      subaction_name: TrackingConstant.PROCESSED_SUBMIT,
      creative_id: getCreativeId(target),
      value: 'Send to Dealer',
    },
  });
}

function fireLeadFormSubmit({ target }) {
  EventToolbox.fireTrackAction({
    event_type: TrackingConstant.EVENT_TYPE_ACTION_PROGRESS,
    event_data: {
      action_cause: TrackingConstant.ACTION_CAUSE_BUTTON_CLICK,
      action_category: TrackingConstant.USER_ACTION_CATEGORY,
      action_name: TrackingConstant.ACTION_LEAD_SUBMISSION,
      subaction_name: TrackingConstant.SUBMIT,
      creative_id: getCreativeId(target),
      value: 'Send to Dealer',
    },
  });
}

function fireDisplayLeadForm({ target }) {
  EventToolbox.fireTrackAction({
    event_type: TrackingConstant.EVENT_TYPE_ACTION_COMPLETED,
    event_data: {
      action_name: TrackingConstant.ACTION_LEAD_SUBMISSION,
      subaction_name: TrackingConstant.DISPLAY_LEAD_FORM,
      creative_id: getCreativeId(target),
    },
  });
}

function fireDisplayThankYou({ target }) {
  EventToolbox.fireTrackAction({
    event_type: TrackingConstant.EVENT_TYPE_ACTION_END,
    event_data: {
      action_name: TrackingConstant.ACTION_LEAD_SUBMISSION,
      subaction_name: TrackingConstant.DISPLAY_THANK_YOU,
      creative_id: getCreativeId(target),
    },
  });
}

function fireSendConsumerEmailTracking({ detail: { emailType } }) {
  EventToolbox.fireTrackAction({
    event_type: TrackingConstant.EVENT_TYPE_ACTION_COMPLETED,
    event_data: {
      action_name: TrackingConstant.ACTION_TOOL_SELECT,
      action_category: TrackingConstant.SYSTEM_ACTION_CATEGORY,
      subaction_name: 'send_marketo_lead_confirm',
      value: CONSUMER_EMAIL_VALUES[emailType],
    },
  });
}

function fireSendConfrimationEmailTracking({ detail: { value } }) {
  EventToolbox.fireTrackAction({
    event_type: TrackingConstant.EVENT_TYPE_ACTION_COMPLETED,
    event_data: {
      action_name: TrackingConstant.ACTION_TOOL_SELECT,
      action_category: TrackingConstant.SYSTEM_ACTION_CATEGORY,
      subaction_name: 'send_marketo_lead_confirm',
      value,
    },
  });
}

function fireCounterTrigger({ detail: { message, counterName, isJoint } }) {
  const value = verifyStatement(isJoint, JOINT, INDIVIDUAL);
  const creativeId = `financing_payments_${value}`;

  const trackData = {
    event_type: TrackingConstant.EVENT_TYPE_ACTION_COMPLETED,
    event_data: {
      action_name: TrackingConstant.ACTION_TOOL_SELECT,
      subaction_name: `counter_${counterName ? COUNTER_TYPE[counterName] : 'other'}`,
      action_cause: TrackingConstant.SYSTEM_ACTION_CAUSE,
      creative_id: creativeId,
      value: message,
    },
  };
  EventToolbox.fireTrackAction(trackData);
}

function fireZipInputTracking({ detail: { valid, value } }) {
  EventToolbox.fireTrackAction({
    event_type: TrackingConstant.EVENT_TYPE_ACTION_COMPLETED,
    event_data: {
      action_name: TrackingConstant.ACTION_TOOL_SELECT,
      subaction_name: `apply_taxes_and_fees${valid ? '' : '_error'}`,
      action_category: TrackingConstant.USER_ACTION_CATEGORY,
      action_cause: TrackingConstant.ACTION_CAUSE_USER_INPUT,
      value,
    },
  });
}

function fireDisplayEmailInputTracking({ target }) {
  EventToolbox.fireTrackAction({
    event_type: TrackingConstant.EVENT_TYPE_ACTION_START,
    event_data: {
      action_name: TrackingConstant.ACTION_SHOW_CONTENT,
      subaction_name: TrackingConstant.DISPLAY_EMAIL_INPUT,
      action_category: TrackingConstant.SYSTEM_ACTION_CATEGORY,
      action_cause: TrackingConstant.PAGE_LOAD_CAUSE,
      creative_id: getCreativeId(target),
    },
  });
}

function fireSaveEmailTracking({ target }) {
  EventToolbox.fireTrackAction({
    event_type: TrackingConstant.EVENT_TYPE_ACTION_PROGRESS,
    event_data: {
      action_name: TrackingConstant.ACTION_SIGN_UP,
      action_category: TrackingConstant.USER_ACTION_CATEGORY,
      action_cause: TrackingConstant.ACTION_CAUSE_BUTTON_CLICK,
      subaction_name: TrackingConstant.SUCCESS_EMAIL_ONLY_SIGN_UP,
      creative_id: getCreativeId(target),
    },
  });
}

function fireListPixel({ detail: { vehicle } }) {
  const type = get(vehicle, 'type', '').toLowerCase();
  const cpoResults = getInventoryTypesCount([vehicle]).CPO;
  const dealerPhotos = get(vehicle, DATA_PATH.VDP_DEALER_PHOTO);

  const trackData = {
    event_type: TrackingConstant.EVENT_TYPE_ACTION_COMPLETED,
    event_data: {
      action_name: TrackingConstant.ACTION_LIST,
      action_cause: TrackingConstant.PAGE_LOAD_CAUSE,
      action_category: TrackingConstant.SYSTEM_ACTION_CATEGORY,
      creative_id: 'edm-entry-vin-overview-financing-vehicle_info',
      loc_id_list: `${get(vehicle, 'dealerInfo.rooftopId', '')}`,
      f_id_list: `${get(vehicle, 'dealerInfo.franchiseId', '')}`,
      inv_id_list: getInvId(vehicle),
      vin_list: getInvVin(vehicle),
      match_type_list: 'local',
      cpo_results: cpoResults,
      new_results: getInventoryTypesCount([vehicle]).NEW,
      used_results: getInventoryTypesCount([vehicle]).USED,
      match_results: 1,
      match_total: 1,
      cpo: verifyStatement(cpoResults, MATCH_RESULT.CPO, MATCH_RESULT.NOT_CPO),
      inv_treatment_list: isPartnerDealer(vehicle) ? `${type}_base` : `${type}_3rdparty`,
      sort_algo_yes_no: 'no',
      sort_algo_name: '',
      price: get(vehicle, DATA_PATH.DEALER_PRICE, get(vehicle, DATA_PATH.PRICE, NEGATIVE)),
      total_price_change: !isHistoryChanged(vehicle)
        ? ''
        : get(vehicle, 'computedDisplayInfo.priceHistoryInfo.totalPriceChange', ''),
      photo_type: verifyStatement(
        dealerPhotos,
        PHOTO_TYPE.DEALER,
        get(vehicle, DATA_PATH.STOCK_PHOTO_PROVIDER, PHOTO_TYPE.NO_IMAGE)
      ),
      days_listed: get(vehicle, DATA_PATH.DAYS_ON_EDMUNDS, DEFAULT_VALUE),
      below_market: get(vehicle, DATA_PATH.BELOW_MARKET, DEFAULT_VALUE),
      deal_indicator: get(vehicle, DATA_PATH.DEAL_INDICATOR, DEFAULT_VALUE),
      five_star_dealer: verifyStatement(get(vehicle, DATA_PATH.FIVE_STAR_DEALER, []).length, POSITIVE, NEGATIVE),
      vehicle_history: getCorrectStatus(
        get(vehicle, DATA_PATH.FREE_HISTORY_REPORT),
        get(vehicle, DATA_PATH.HISTORY_PROVIDER, DEFAULT_VALUE)
      ),
      recommended_offer: get(vehicle, DATA_PATH.RECOMMENDED_OFFER, DEFAULT_VALUE),
      dealer_rating: get(vehicle, DATA_PATH.DEALER_RATING),
      subaction_name: TrackingConstant.PREQUALIFY,
      ts: Date.now(),
    },
  };

  EventToolbox.fireTrackAction(trackData);
}

function fireVinConfirmation({ detail: { isMatchingVIN } }) {
  EventToolbox.fireTrackAction({
    event_type: TrackingConstant.EVENT_TYPE_ACTION_COMPLETED,
    event_data: {
      action_name: TrackingConstant.ACTION_FILL_FORM,
      action_cause: TrackingConstant.EVENT_TYPE_PAGE_ENTER,
      action_category: TrackingConstant.USER_ACTION_CATEGORY,
      subaction_name: isMatchingVIN
        ? TrackingConstant.RATE_LOOKUP_SUCCESS
        : TrackingConstant.RATE_LOOKUP_SUCCESS_NO_VIN,
      creative_id: 'financing_results',
    },
  });
}

function fireConfirmationBtnClick({ detail: { isError, value } }) {
  EventToolbox.fireTrackAction({
    event_type: TrackingConstant.EVENT_TYPE_ACTION_COMPLETED,
    event_data: {
      action_name: TrackingConstant.VIEW_SEARCH_RESULT,
      action_cause: TrackingConstant.ACTION_CAUSE_BUTTON_CLICK,
      action_category: TrackingConstant.USER_ACTION_CATEGORY,
      subaction_name: TrackingConstant.SHOP_WITH_PREQUALIFICATION,
      creative_id: isError ? 'financing_results_vin_not_found' : 'financing_results_novin',
      value,
    },
  });
}

function fireWidgetViewTracking({ detail: { formName } }) {
  const creativeId = updateCreativeIdForConfirmMode(`review_${formName.toLowerCase()}`, formName, true);
  const pageHeight = Math.max(document.body.scrollHeight, document.body.offsetHeight);

  EventToolbox.fireTrackAction({
    event_type: TrackingConstant.EVENT_TYPE_WIDGET_VIEW,
    event_data: {
      action_name: TrackingConstant.ACTION_WIDGET_VIEW,
      action_cause: TrackingConstant.ACTION_CAUSE_SCROLL,
      creative_id: creativeId,
      total_page_height: pageHeight,
    },
  });
}

function fireConfirmClickTracking({ detail: { formName, value } }) {
  const creativeId = updateCreativeIdForConfirmMode(`review_${formName.toLowerCase()}`, formName, true);

  EventToolbox.fireTrackAction({
    event_type: TrackingConstant.EVENT_TYPE_ACTION_PROGRESS,
    event_data: {
      action_name: TrackingConstant.ACTION_FILL_FORM,
      subaction_name: TrackingConstant.SUBACTION_CONFIRM_ENTRY,
      action_category: TrackingConstant.USER_ACTION_CATEGORY,
      action_cause: TrackingConstant.ACTION_CAUSE_BUTTON_CLICK,
      creative_id: creativeId,
      value: value.toLowerCase(),
    },
  });
}

function fireEditConfirmClickTracking({ detail: { formName } }) {
  const creativeId = updateCreativeIdForConfirmMode(`review_${formName.toLowerCase()}`, formName, true);

  EventToolbox.fireTrackAction({
    event_type: TrackingConstant.EVENT_TYPE_ACTION_PROGRESS,
    event_data: {
      action_name: TrackingConstant.ACTION_FILL_FORM,
      subaction_name: TrackingConstant.SUBACTION_EDIT_ENTRY,
      action_category: TrackingConstant.USER_ACTION_CATEGORY,
      action_cause: TrackingConstant.ACTION_CAUSE_LINK_CLICK,
      creative_id: creativeId,
      value: 'edit',
    },
  });
}

function fireInputValueTracking({ detail: { element, data } }) {
  const { isJoint, isPrimary, formName, fieldName } = data;
  const type = verifyStatement(isJoint, JOINT, INDIVIDUAL);
  const subactionName = `${fieldName}${isPrimary ? 'Primary' : ''}_input_success`;
  const creativeId = `${formName}_${type}`;
  const amountValue = element.value.replace(/\D/g, '');
  const value = Number(amountValue) === 0 ? 0 : Number(`1e${amountValue.length - 1}`);

  EventToolbox.fireTrackAction({
    event_type: TrackingConstant.EVENT_TYPE_ACTION_PROGRESS,
    event_data: {
      action_name: TrackingConstant.ACTION_FILL_FORM,
      action_cause: TrackingConstant.ACTION_CAUSE_USER_INPUT,
      action_category: TrackingConstant.USER_ACTION_CATEGORY,
      subaction_name: subactionName,
      creative_id: creativeId,
      value,
    },
  });
}

function fireLookItUpClick({ detail: { isMatchingVIN, step, isPreVinFlow } }) {
  EventToolbox.fireTrackAction({
    event_type: TrackingConstant.EVENT_TYPE_ACTION_START,
    event_data: {
      action_name: TrackingConstant.ACTION_FILL_FORM,
      action_cause: TrackingConstant.ACTION_CAUSE_LINK_CLICK,
      action_category: TrackingConstant.USER_ACTION_CATEGORY,
      subaction_name: TrackingConstant.SUBACTION_START_PERSONAL_LOOKUP,
      creative_id: updateCreativeId({ isPreVinFlow, isMatchingVIN, trackingParent: step }),
      value: 'Look it up',
    },
  });
}

function fireApiSystemErrorTracking({ detail: { financingResponse } }) {
  const response = get(financingResponse, 'response', []);
  const apiCalls = get(financingResponse, 'api', []);

  forEach(apiCalls, (api, index) => {
    const errorCode = get(response[index], 'status');

    if (errorCode && errorCode !== HTTP_OK) {
      EventToolbox.fireTrackAction({
        event_type: TrackingConstant.EVENT_TYPE_ACTION_PROGRESS,
        event_data: {
          action_name: TrackingConstant.ACTION_FILL_FORM,
          action_cause: TrackingConstant.ACTION_CAUSE_USER_INPUT,
          action_category: TrackingConstant.USER_ACTION_CATEGORY,
          subaction_name: TrackingConstant.API_SYSTEM_ERROR,
          creative_id: api.replace(/.*\/financing\//, '').replace(/\/$/, ''),
          value: `${errorCode}. ${get(response[index], 'custom.message', get(response[index], 'custom.error'))}`,
        },
      });
    }
  });
}

function firePrequalNewappApprovalTracking({ detail: { isMatchingVIN, vehicle, isPreVinFlow } }) {
  EventToolbox.fireTrackAction({
    event_type: TrackingConstant.EVENT_TYPE_ACTION_COMPLETED,
    event_data: {
      action_name: TrackingConstant.ACTION_FILL_FORM,
      action_cause: TrackingConstant.ACTION_CAUSE_BUTTON_CLICK,
      action_category: TrackingConstant.USER_ACTION_CATEGORY,
      subaction_name: TrackingConstant.PREQUAL_NEWAPP_APPROVAL,
      value: isPreVinFlow
        ? 'previn_nodealer'
        : `${isPartnerDealer(vehicle) ? 'partner' : 'basic'}_${isMatchingVIN ? 'matched' : 'unmatched'}`,
    },
  });
}

function fireApiSystemErrorTrackingV2({ detail: { financingResponse } }) {
  const response = financingResponse?.response;
  const errorCode = response?.status ?? response?.statusCode;

  if (errorCode && errorCode !== HTTP_OK) {
    EventToolbox.fireTrackAction({
      event_type: TrackingConstant.EVENT_TYPE_ACTION_PROGRESS,
      event_data: {
        action_name: TrackingConstant.ACTION_FILL_FORM,
        action_cause: TrackingConstant.ACTION_CAUSE_USER_INPUT,
        action_category: TrackingConstant.USER_ACTION_CATEGORY,
        subaction_name: TrackingConstant.API_SYSTEM_ERROR,
        creative_id: financingResponse?.api?.replace(/.*\/financing\//, '')?.replace(/\/$/, ''),
        value: `${errorCode}. ${response?.custom?.message || response?.custom?.error || response?.message}`,
      },
    });
  }
}

function fireListPixelPostprequal({ detail: { payment, creativeId } }) {
  EventToolbox.fireTrackAction({
    event_type: TrackingConstant.EVENT_TYPE_ACTION_COMPLETED,
    event_data: {
      action_name: TrackingConstant.ACTION_LIST,
      action_cause: TrackingConstant.PAGE_LOAD_CAUSE,
      action_category: TrackingConstant.SYSTEM_ACTION_CATEGORY,
      lender_name: 'COF',
      match_results: 1,
      monthly_payments: payment?.monthlyPayments,
      aprs: payment?.aprs,
      terms: payment?.terms,
      creative_id: creativeId,
    },
  });
}

function fireCalculatePaymentPostprequal({ detail: { creativeId, value, subactionName } }) {
  EventToolbox.fireTrackAction({
    event_type: TrackingConstant.EVENT_TYPE_ACTION_COMPLETED,
    event_data: {
      action_name: TrackingConstant.CALCULATE_PAYMENT,
      subaction_name: subactionName,
      action_cause: TrackingConstant.PAGE_LOAD_CAUSE,
      action_category: TrackingConstant.SYSTEM_ACTION_CATEGORY,
      creative_id: creativeId,
      value,
    },
  });
}

function fireWidgetViewPhotoflipperTracking({ detail: { creativeId } }) {
  EventToolbox.fireTrackAction({
    event_type: TrackingConstant.EVENT_TYPE_WIDGET_VIEW,
    event_data: {
      action_name: TrackingConstant.ACTION_WIDGET_VIEW,
      action_cause: TrackingConstant.ACTION_CAUSE_DRAWER_OPEN,
      creative_id: creativeId,
    },
  });
}

function fireFeesDisclaimerTracking({ detail: { disclaimer } }) {
  EventToolbox.fireTrackAction({
    event_type: TrackingConstant.EVENT_TYPE_ACTION_COMPLETED,
    event_data: {
      action_name: TrackingConstant.ACTION_SHOW_CONTENT,
      subaction_name: TrackingConstant.SUBACTION_DISPLAY_FEE_DISCLAIMER,
      action_cause: TrackingConstant.PAGE_LOAD_CAUSE,
      action_category: TrackingConstant.SYSTEM_ACTION_CATEGORY,
      value: disclaimer,
    },
  });
}

export const FinancingEngagementHandler = {
  init: () => {
    EventToolbox.on(FINANCING_EVENTS.INPUT_VALIDATION, fireFormFieldTracking);
    EventToolbox.on(FINANCING_EVENTS.CALCULATE_PAYMENTS, fireCalculatePaymentsButton);
    EventToolbox.on(FINANCING_EVENTS.PAYMENT_LIST_PIXEL, firePaymentListPixel);
    EventToolbox.on(FINANCING_EVENTS.NEXT_STEP_BTN, fireNextStepBtn);
    EventToolbox.on(FINANCING_EVENTS.SAVE_VEHICLE, fireSaveVehicle);
    EventToolbox.on(FINANCING_EVENTS.BACK_BTN, fireBackBtn);
    EventToolbox.on(FINANCING_EVENTS.LEAD_FORM_ATTEMPT_SUBMIT, fireLeadFormAttemptSubmit);
    EventToolbox.on(FINANCING_EVENTS.LEAD_FORM_PROCESSED_SUBMIT, fireLeadFormProcessedSubmit);
    EventToolbox.on(FINANCING_EVENTS.LEAD_FORM_SUBMIT, fireLeadFormSubmit);
    EventToolbox.on(FINANCING_EVENTS.DISPLAY_LEAD_FORM, fireDisplayLeadForm);
    EventToolbox.on(FINANCING_EVENTS.DISPLAY_THANK_YOU, fireDisplayThankYou);
    EventToolbox.on(FINANCING_EVENTS.SEND_CONFIRMATION_EMAIL, fireSendConfrimationEmailTracking);
    EventToolbox.on(FINANCING_EVENTS.COUNTER_TRIGGER, fireCounterTrigger);
    EventToolbox.on(FINANCING_EVENTS.SEND_CONSUMER_EMAIL, fireSendConsumerEmailTracking);
    EventToolbox.on(FINANCING_EVENTS.DISPLAY_EMAIL_INPUT, fireDisplayEmailInputTracking);
    EventToolbox.on(FINANCING_EVENTS.SAVE_EMAIL_EVENT, fireSaveEmailTracking);
    EventToolbox.on(FINANCING_EVENTS.LIST_PIXEL, fireListPixel);
    EventToolbox.on(FINANCING_EVENTS.VIN_CONFIRMATION, fireVinConfirmation);
    EventToolbox.on(FINANCING_EVENTS.CONFIRMATION_CTA_CLICK, fireConfirmationBtnClick);
    EventToolbox.on(FINANCING_EVENTS.LOOK_IT_UP_CLICK, fireLookItUpClick);
    EventToolbox.on(FINANCING_EVENTS.WIDGET_VIEW, fireWidgetViewTracking);
    EventToolbox.on(FINANCING_EVENTS.CONFIRM_CLICK, fireConfirmClickTracking);
    EventToolbox.on(FINANCING_EVENTS.EDIT_CONFIRM_CLICK, fireEditConfirmClickTracking);
    EventToolbox.on(FINANCING_EVENTS.INPUT_VALUE, fireInputValueTracking);
    EventToolbox.on(FINANCING_EVENTS.API_SYSTEM_ERROR, fireApiSystemErrorTracking);
    EventToolbox.on(FINANCING_EVENTS.API_SYSTEM_ERROR_V2, fireApiSystemErrorTrackingV2);
    EventToolbox.on(FINANCING_EVENTS.PREQUAL_NEWAPP_APPROVAL, firePrequalNewappApprovalTracking);
    EventToolbox.on(FINANCING_EVENTS.ZIP_INPUT_VALUE, fireZipInputTracking);
    EventToolbox.on(FINANCING_EVENTS.LIST_PIXEL_POSTPREQUAL, fireListPixelPostprequal);
    EventToolbox.on(FINANCING_EVENTS.CALCULATE_PAYMENT_POSTPREQUAL, fireCalculatePaymentPostprequal);
    EventToolbox.on(FINANCING_EVENTS.WIDGET_VIEW_PHOTOFLIPPER, fireWidgetViewPhotoflipperTracking);
    EventToolbox.on(FINANCING_EVENTS.FEES_DISCLAIMER, fireFeesDisclaimerTracking);
  },
};
